import { Box } from "@mui/material";
import { Input } from "components";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm, useWatch } from "react-hook-form";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import toast from "react-hot-toast";
import {
  useCreateExpenseConfig,
  useGlobalStore,
  useUpdateExpenseConfig,
} from "controller";
import { Decimal } from "utilz/decimal";

const schema = yup.object({
  expense_name: yup
    .string()
    .trim()
    .max(50, "Expense name cannot exceed 50 characters")
    .required("Expense name is required"),
  price: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .max(10000000, "Amount cannot exceed 100,000,00")
    .typeError("Amount must be a number")
    .required("Amount is required"),
});

export const BillingExpenseFields = forwardRef(
  ({ data, closeDrawer, mode }: any, ref) => {
    useImperativeHandle(ref, () => ({
      saveButtonClick: () => {
        handleSubmit(async (data: any) => {
          const decimalPrice = new Decimal(data.price);
          const newData = {
            ...data,
            price: decimalPrice.toDecimalPlaces(2).mul(100).toNumber(),
          }; // dollars converted to cents - backend saves all prices in cents
          try {
            if (mode?.method === "create") {
              await createExpenseConfig(newData);
              toast.success("Expense added successfully");
            } else if (mode?.method === "update") {
              await updateExpenseConfig({
                data: newData,
                id: mode?.data?.mapping_id,
              });
              toast.success("Expense updated successfully");
            }
            closeDrawer();
            reset();
          } catch (error: any) {
            const errorMessage = error?.message || "Something went wrong";
            toast.error(errorMessage);
          }
        })();
      },
      cancelButtonClick: () => {
        closeDrawer();
        reset();
      },
    }));

    const currency = useGlobalStore((state) => state.currency);

    const {
      handleSubmit,
      control,
      reset,
      formState: { errors },
      setValue,
    } = useForm({
      defaultValues: {
        expense_name: data?.expense_name || "",
        price: data?.price / 100 || "",
      },
      resolver: yupResolver(schema),
    });

    useEffect(() => {
      if (mode?.data) {
        // Manually set default values after mode?.data is fetched
        setValue("expense_name", mode?.data.expense_name || "");
        setValue("price", mode?.data.price / 100 || "");
      } else {
        reset();
      }
    }, [mode?.data, setValue, closeDrawer]);

    const { mutateAsync: createExpenseConfig, isLoading } =
      useCreateExpenseConfig();
    const { mutateAsync: updateExpenseConfig } = useUpdateExpenseConfig();

    return (
      <Box
        sx={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <Input
          label={"Expense Name"}
          name="expense_name"
          control={control}
          error={errors?.expense_name ? true : false}
          isResponsive={false}
          // @ts-ignore
          errorMessage={errors?.expense_name?.message}
        />
        <Controller
          control={control}
          name="price"
          render={({ field }) => (
            <Input
              label={"Amount"}
              name="price"
              placeholder={`${currency?.symbol || ""}0.00`}
              error={errors?.price ? true : false}
              isResponsive={false}
              errorMessage={errors?.price?.message}
              type="number"
              value={field.value}
              onChange={(e) => {
                if (e.target.value.includes(".")) {
                  const parts = e.target.value.split(".");
                  if (parts[1] && parts[1].length > 2) {
                    e.preventDefault();
                    return;
                  }
                }
                field.onChange(e);
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "+" ||
                  e.key === "-"
                ) {
                  e.preventDefault();
                }
              }}
            />
          )}
        />
      </Box>
    );
  },
);
